/**
 * "Show invisible lines" (W2-02; DESIGN.md §4.8, VIEW-03): lines Phira draws nothing for at the playhead (opacity
 * <= 0, zero size, empty text, UI movers: `LineStateRt.drawsNothing`), e.g. the helper parents of an RPE-style
 * 24-line start, as dashed outlines with their label and the reason on hover. They are clickable for activation and
 * the gizmo (selection.ts and gizmo.ts pick them while this toggle is on).
 */

import type { AppServices } from '../../../app/registry/types';
import type { LineStateRt } from '../../../core/engine/types';
import type { OverlayDrawContext, OverlayHit, PreviewOverlayProvider } from '../../../render/types';
import { filteredOut } from '../filters';
import { m } from '../preview.messages';
import {
  dp,
  lineLabel,
  lineShape,
  PICK_TOLERANCE,
  placeLabel,
  shapeCentre,
  shapeDistance,
  strokeShape,
} from './shared';
import { overlayTheme } from './theme';

/** Most invisible lines outlined per frame (an RPE-style start has 23; storyboards may have hundreds). */
const MAX_OUTLINES = 600;

/** Message key of the reason a line draws nothing. */
export function invisibleReasonKey(s: Pick<LineStateRt, 'drawsNothing'>): string {
  switch (s.drawsNothing) {
    case 'alpha':
      return m.invisibleAlpha;
    case 'scale':
      return m.invisibleScale;
    case 'emptyText':
      return m.invisibleEmptyText;
    case 'paintCleared':
      return m.invisiblePaintCleared;
    default:
      return m.invisibleUiMover;
  }
}

export function createInvisibleLinesOverlay(services: AppServices): PreviewOverlayProvider {
  const enabled = (): boolean => services.editor.getState().preview.showInvisibleLines;
  const label = (s: LineStateRt): string => lineLabel(s.index, services.store?.doc.lines.get(s.line)?.name ?? '');
  const skip = (s: LineStateRt): boolean => {
    const f = services.editor.getState().filters;
    return filteredOut(f, s.line);
  };
  return {
    id: 'preview.invisibleLines',
    order: 20,
    draw(c: OverlayDrawContext) {
      if (!enabled()) return;
      const store = services.store;
      const doc = store?.doc ?? null;
      const idx = store?.idx ?? null;
      const theme = overlayTheme();
      const active = services.editor.getState().activeLine;
      let drawn = 0;
      for (const s of c.lines) {
        if (s.drawsNothing === null || skip(s)) continue;
        if (drawn++ >= MAX_OUTLINES) break;
        const shape = lineShape(c, s, doc, idx);
        const isActive = s.line === active;
        strokeShape(c, shape, isActive ? theme.accent : theme.invisible, isActive ? 2 : 1.25, true);
        const [x, y] = shapeCentre(shape);
        // Labels take a free spot or are left out (helper parents often share one point, UX-052).
        placeLabel(
          c,
          label(s),
          x,
          y,
          { fg: theme.textMuted, bg: theme.surface, border: isActive ? theme.accent : theme.invisible, size: 10 },
          isActive,
        );
      }
    },
    hitTest(c, px, py): OverlayHit | null {
      if (!enabled()) return null;
      const store = services.store;
      const tol = dp(c, PICK_TOLERANCE);
      for (const s of c.lines) {
        if (s.drawsNothing === null || skip(s)) continue;
        if (shapeDistance(lineShape(c, s, store?.doc ?? null, store?.idx ?? null), px, py) > tol) continue;
        const element = services.store?.doc.lines.get(s.line)?.attachUI ?? '';
        return {
          provider: 'preview.invisibleLines',
          cursor: 'pointer',
          data: { tooltip: { key: invisibleReasonKey(s), params: { label: label(s), element } }, line: s.line },
        };
      }
      return null;
    },
  };
}
